import { ACTION_LABEL_FALLBACK, getActionLabelKey } from './actionItemLabel.ts';
import type {
	Meeting,
	MeetingActionConfluenceContentType,
	MeetingActionItem,
	MeetingActionStatus,
	MeetingActionType,
	MeetingPerson,
} from './meetingTypes.ts';

/**
 * Shape of a single meeting node as selected by `useMeetingsQuery`.
 * Kept structural (not importing the generated type) so the mapper is easy to
 * unit test with plain fixtures; the hook passes the generated nodes in.
 */
export type RawLoomPerson = {
	accountId?: string | null;
	name?: string | null;
	picture?: string | null;
} | null;

export type RawLoomOneClickAction = {
	id?: string | null;
	action?: string | null;
	type?: string | null;
	confluenceContentType?: string | null;
	status?: string | null;
	timestampSeconds?: number | null;
} | null;

export type RawLoomMeeting = {
	id?: string | null;
	title?: string | null;
	startsAt?: string | null;
	recorderId?: string | null;
	recorder?: RawLoomPerson;
	inviteeAris?: ReadonlyArray<string | null> | null;
	invitees?: ReadonlyArray<RawLoomPerson> | null;
	video?: {
		id?: string | null;
		url?: string | null;
		isMeeting?: boolean | null;
		meetingAri?: string | null;
		shortSummary?: string | null;
	} | null;
	oneClickActions?: ReadonlyArray<RawLoomOneClickAction> | null;
} | null;

const toPerson = (raw: RawLoomPerson, fallbackAccountId = ''): MeetingPerson => ({
	accountId: raw?.accountId ?? fallbackAccountId,
	displayName: raw?.name ?? 'Unknown',
	avatarUrl: raw?.picture ?? undefined,
});

const ACTION_TYPES: readonly MeetingActionType[] = ['CONFLUENCE', 'JIRA', 'MESSAGE'];
const CONTENT_TYPES: readonly MeetingActionConfluenceContentType[] = [
	'LIVE_DOC',
	'WHITEBOARD',
	'DATABASE',
	'SLIDES',
];
const ACTION_STATUSES: readonly MeetingActionStatus[] = ['PENDING', 'ACCEPTED', 'DISMISSED'];

/** Normalise a raw enum string (any case, e.g. "live_doc") to the typed union. */
const asEnum = <T extends string>(
	raw: string | null | undefined,
	allowed: readonly T[],
): T | undefined => {
	const upper = raw?.toUpperCase();
	return allowed.find((v) => v === upper);
};

/**
 * Map a raw `LoomMeetingOneClickAction` node into the UI `MeetingActionItem`.
 * Returns `null` for actions that are DISMISSED (filtered out) or that lack the
 * fields needed to render.
 */
const toActionItem = (raw: RawLoomOneClickAction): MeetingActionItem | null => {
	if (!raw?.id) {
		return null;
	}
	const status = asEnum(raw.status, ACTION_STATUSES) ?? 'PENDING';
	// Dismissed actions are never rendered.
	if (status === 'DISMISSED') {
		return null;
	}
	const type = asEnum(raw.type, ACTION_TYPES);
	// `raw.action` is the AGG-provided descriptive line (e.g. "Turn this into a
	// PRD"). It remains the item's `content`; the CTA button text is derived
	// deterministically from the typed enums below.
	const content = raw.action ?? '';
	const confluenceContentType =
		type === 'CONFLUENCE' ? asEnum(raw.confluenceContentType, CONTENT_TYPES) : undefined;
	// A CTA payload is only meaningful when we know the target product.
	const action = type
		? (() => {
				const labelKey = getActionLabelKey({ type, confluenceContentType });
				return {
					labelKey,
					label: ACTION_LABEL_FALLBACK[labelKey],
					type,
					confluenceContentType,
				};
			})()
		: undefined;
	return {
		id: raw.id,
		content,
		timestampSeconds: raw.timestampSeconds ?? undefined,
		status,
		action,
	};
};

/**
 * Map a raw `LoomMeeting` node into the UI `Meeting` domain type.
 *
 * The caller is expected to have already filtered out meetings without a
 * playable `video.url`, so `videoUrl`/`videoAri` are safe to default to ''.
 * Action items are mapped from the meeting's `oneClickActions`, with DISMISSED
 * items filtered out (PENDING + ACCEPTED are kept for the UI to render).
 */
export const mapLoomMeeting = (raw: RawLoomMeeting): Meeting => ({
	id: raw?.id ?? '',
	title: raw?.title ?? 'Untitled meeting',
	startsAt: raw?.startsAt ?? '',
	recorderId: raw?.recorderId ?? '',
	owner: toPerson(raw?.recorder ?? null, raw?.recorderId ?? ''),
	invitees: (raw?.invitees ?? [])
		.filter((p): p is NonNullable<RawLoomPerson> => Boolean(p))
		.map((p) => toPerson(p)),
	videoAri: raw?.video?.id ?? '',
	videoUrl: raw?.video?.url ?? '',
	summary: raw?.video?.shortSummary ?? undefined,
	actionItems: (raw?.oneClickActions ?? [])
		.map(toActionItem)
		.filter((item): item is MeetingActionItem => item !== null),
});
